import { useCallback, useEffect, useRef } from 'react';

export const REFRESH_RATE_120 = 1000 / 120;

type ThrottledFunction<T extends unknown[]> = (...args: T) => void;

export function useThrottle<T extends unknown[]>(
  fn: ThrottledFunction<T>,
  delay: number,
  deps: unknown[] = [],
): ThrottledFunction<T> {
  const fnRef = useRef<ThrottledFunction<T>>(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastExecutedTimeRef = useRef<number | null>(null);

  // Update the latest function
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const throttledFn = useCallback(
    (...args: T) => {
      const now = Date.now();

      const execute = () => {
        lastExecutedTimeRef.current = now;
        fnRef.current(...args);
      };

      if (lastExecutedTimeRef.current === null) {
        execute();
        return;
      }

      const timeSinceLastExecution = now - (lastExecutedTimeRef.current || 0);

      if (timeSinceLastExecution >= delay) {
        execute();
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        execute();
      }, delay - timeSinceLastExecution);
    },
    // Include dependencies array here
    [delay, ...deps],
  );

  // Clean up timer on unmount or when dependencies change
  // biome-ignore lint/correctness/useExhaustiveDependencies: keep in sync with throttledFn dependencies
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [delay, ...deps],
  );

  return throttledFn;
}
